// HRM: Departments, Designations, Office Shifts, Employees, Attendance, Leaves, Holidays, Payroll
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Plus, Pencil, Trash2, Search, X, ArrowLeft, Save, Users, Building2,
  BadgeCheck, Clock, CalendarCheck, CalendarX, PartyPopper, Wallet, Check,
} from "lucide-react";
import { api } from "../../../lib/auth";
import { Card, PageHeader, PrimaryButton, Field, TextInput, Select, SectionHeader } from "../products/_shared";
import { fmtMoney as fmt } from "../../../lib/currency";

// ────────────────────────── HRM DASHBOARD ──────────────────────────
export function HRMDashboard() {
  const { subdomain } = useParams();
  const [stats, setStats] = useState({});
  useEffect(() => { api.get("/t/hrm/overview").then((r) => setStats(r.data)); }, []);
  
  const tiles = [
    { lbl: "Employees", v: stats.employees, icon: Users, to: "hrm/employees", color: "from-emerald-400 to-emerald-600", shadow: "shadow-emerald-500/20" },
    { lbl: "Departments", v: stats.departments, icon: Building2, to: "hrm/departments", color: "from-sky-400 to-sky-600", shadow: "shadow-sky-500/20" },
    { lbl: "Designations", v: stats.designations, icon: BadgeCheck, to: "hrm/designations", color: "from-violet-400 to-violet-600", shadow: "shadow-violet-500/20" },
    { lbl: "Pending Leaves", v: stats.pending_leaves, icon: CalendarX, to: "hrm/leaves", color: "from-amber-400 to-amber-600", shadow: "shadow-amber-500/20" },
    { lbl: "Holidays", v: stats.holidays, icon: PartyPopper, to: "hrm/holidays", color: "from-pink-400 to-pink-600", shadow: "shadow-pink-500/20" },
    { lbl: "Pending Payrolls", v: stats.payrolls_pending, icon: Wallet, to: "hrm/payrolls", color: "from-indigo-400 to-indigo-600", shadow: "shadow-indigo-500/20" },
  ];

  return (
    <div data-testid="hrm-dashboard-page" className="p-4 md:p-6 lg:p-8 min-h-screen bg-gray-50/50">
      <div className="max-w-7xl mx-auto">
        <PageHeader title="Human Resources" breadcrumb="HRM / Dashboard" />
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-6 mt-6">
          {tiles.map((t) => {
            const Icon = t.icon;
            return (
              <Link key={t.lbl} to={`/t/${subdomain}/${t.to}`}
                className="group bg-white rounded-2xl border border-gray-200 p-5 hover:border-transparent hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1 relative overflow-hidden">
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${t.color} text-white flex items-center justify-center shadow-lg ${t.shadow} mb-4 transition-transform group-hover:scale-110`}>
                  <Icon size={22} strokeWidth={2.5} />
                </div>
                <div className="text-3xl font-black tracking-tight text-gray-900">{t.v ?? 0}</div>
                <div className="text-xs font-medium text-gray-500 mt-1 uppercase tracking-wide">{t.lbl}</div>
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ────────────────────────── Generic single-table CRUD ──────────────────────────
function SimpleTableCRUD({ title, breadcrumb, endpoint, fields, testidBase, columns, icon: Icon, onAfterChange }) {
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState({});

  const load = () => api.get(endpoint).then((r) => setRows(r.data));
  useEffect(() => { load(); }, []); // eslint-disable-line

  const openCreate = () => {
    const blank = {};
    fields.forEach((f) => { blank[f.name] = f.default ?? ""; });
    setForm(blank); setEdit(null); setOpen(true);
  };
  const openEdit = (r) => { setForm({ ...r }); setEdit(r); setOpen(true); };
  const submit = async () => {
    try {
      if (edit) await api.patch(`${endpoint}/${edit.id}`, form);
      else await api.post(endpoint, form);
      toast.success(edit ? "Updated successfully" : "Created successfully");
      setOpen(false); load(); onAfterChange?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const del = async (r) => {
    if (!window.confirm(`Are you sure you want to delete ${r.name}?`)) return;
    await api.delete(`${endpoint}/${r.id}`);
    toast.success("Deleted successfully"); load(); onAfterChange?.();
  };

  const filtered = search ? rows.filter((r) => (r.name || "").toLowerCase().includes(search.toLowerCase())) : rows;

  return (
    <div data-testid={`${testidBase}-page`} className="p-4 md:p-6 lg:p-8 min-h-screen bg-gray-50/50">
      <div className="max-w-7xl mx-auto">
        <PageHeader title={title} breadcrumb={breadcrumb} actions={
          <PrimaryButton icon={Plus} onClick={openCreate} testid={`${testidBase}-new`} className="shadow-sm">Create New</PrimaryButton>
        } />
        
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden mt-6">
          <div className="p-4 border-b border-gray-100 bg-gray-50/50 flex items-center justify-between">
            <div className="flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-200 rounded-xl focus-within:ring-4 focus-within:ring-[#1FA868]/10 focus-within:border-[#1FA868] transition-all max-w-md w-full shadow-sm">
              <Search size={18} className="text-gray-400" />
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={`Search ${title.toLowerCase()}...`}
                data-testid={`${testidBase}-search`} className="flex-1 bg-transparent border-none outline-none text-sm text-gray-900 placeholder-gray-400" />
            </div>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead className="bg-gray-50 text-gray-500 text-xs uppercase tracking-wider font-semibold border-b border-gray-100">
                <tr>
                  {columns.map((c) => <th key={c.key} className="px-6 py-4">{c.lbl}</th>)}
                  <th className="px-6 py-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.map((r) => (
                  <tr key={r.id} className="hover:bg-gray-50/80 transition-colors group">
                    {columns.map((c) => (
                      <td key={c.key} className="px-6 py-4 text-gray-700">{c.render ? c.render(r) : (r[c.key] ?? "—")}</td>
                    ))}
                    <td className="px-6 py-4 text-right opacity-0 group-hover:opacity-100 transition-opacity">
                      <button onClick={() => openEdit(r)} className="p-2 rounded-lg text-gray-400 hover:text-[#1FA868] hover:bg-emerald-50 transition-colors mr-1"><Pencil size={16} /></button>
                      <button onClick={() => del(r)} className="p-2 rounded-lg text-gray-400 hover:text-rose-600 hover:bg-rose-50 transition-colors"><Trash2 size={16} /></button>
                    </td>
                  </tr>
                ))}
                {!filtered.length && <tr><td colSpan={columns.length + 1} className="px-6 py-12 text-center text-gray-400">No {title.toLowerCase()} found.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-900/40 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-[0_20px_50px_rgb(0,0,0,0.1)] overflow-hidden flex flex-col max-h-[90vh]">
            <div className="flex items-center justify-between p-5 border-b border-gray-100 bg-gray-50/50">
              <div className="flex items-center gap-3">
                {Icon && <div className="p-2 bg-emerald-100 text-[#1FA868] rounded-lg"><Icon size={18} /></div>}
                <h3 className="font-bold text-gray-900 text-lg tracking-tight">{edit ? "Edit" : "New"} {title.replace(/s$/, "")}</h3>
              </div>
              <button onClick={() => setOpen(false)} className="p-2 hover:bg-gray-200 text-gray-500 rounded-lg transition-colors"><X size={18} /></button>
            </div>
            
            <div className="p-6 space-y-5 overflow-y-auto custom-scrollbar">
              {fields.map((f) => (
                <Field key={f.name} lbl={f.lbl} required={f.required}>
                  {f.type === "select" ? (
                    <Select value={form[f.name] || ""} onChange={(v) => setForm((x) => ({ ...x, [f.name]: v }))} options={f.options || []} testid={`${testidBase}-f-${f.name}`} />
                  ) : f.type === "textarea" ? (
                    <textarea value={form[f.name] || ""} onChange={(e) => setForm((x) => ({ ...x, [f.name]: e.target.value }))}
                      data-testid={`${testidBase}-f-${f.name}`}
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#1FA868] focus:ring-4 focus:ring-[#1FA868]/10 outline-none text-sm text-gray-900 bg-gray-50 focus:bg-white transition-all min-h-[100px] resize-y" />
                  ) : (
                    <TextInput type={f.type || "text"} value={form[f.name]} onChange={(v) => setForm((x) => ({ ...x, [f.name]: v }))} placeholder={f.placeholder} testid={`${testidBase}-f-${f.name}`} />
                  )}
                </Field>
              ))}
            </div>
            
            <div className="flex justify-end gap-3 p-5 border-t border-gray-100 bg-gray-50/50">
              <button onClick={() => setOpen(false)} className="px-4 py-2.5 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-200 transition-colors">Cancel</button>
              <PrimaryButton icon={Save} onClick={submit} testid={`${testidBase}-save`} className="shadow-sm">Save Changes</PrimaryButton>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ────────────────────────── Concrete HRM sub-pages ──────────────────────────
export function HRMDepartments() {
  return <SimpleTableCRUD
    title="Departments" breadcrumb="HRM / Departments" endpoint="/t/hrm/departments" testidBase="hrm-dept" icon={Building2}
    fields={[
      { name: "name", lbl: "Department Name", required: true, placeholder: "e.g. Engineering" },
      { name: "head", lbl: "Department Head", placeholder: "e.g. Jane Doe" },
      { name: "description", lbl: "Description", type: "textarea", placeholder: "Briefly describe the department's role..." },
    ]}
    columns={[
      { key: "name", lbl: "Department", render: (r) => <span className="font-bold text-gray-900">{r.name}</span> },
      { key: "head", lbl: "Department Head" },
      { key: "description", lbl: "Description", render: (r) => <span className="text-gray-500 line-clamp-1">{r.description || "—"}</span> },
    ]} />;
}

export function HRMDesignations() {
  const [depts, setDepts] = useState([]);
  useEffect(() => { api.get("/t/hrm/departments").then((r) => setDepts(r.data)); }, []);
  return <SimpleTableCRUD
    title="Designations" breadcrumb="HRM / Designations" endpoint="/t/hrm/designations" testidBase="hrm-desig" icon={BadgeCheck}
    fields={[
      { name: "name", lbl: "Designation Title", required: true, placeholder: "e.g. Senior Developer" },
      { name: "department_id", lbl: "Department", type: "select", options: depts.map((d) => ({ value: d.id, label: d.name })) },
      { name: "description", lbl: "Description", type: "textarea" },
    ]}
    columns={[
      { key: "name", lbl: "Designation", render: (r) => <span className="font-bold text-gray-900">{r.name}</span> },
      { key: "department_name", lbl: "Department", render: (r) => <span className="inline-flex items-center px-2.5 py-0.5 rounded-md bg-gray-100 text-gray-700 font-medium text-xs">{r.department_name}</span> },
      { key: "description", lbl: "Description", render: (r) => <span className="text-gray-500 line-clamp-1">{r.description || "—"}</span> },
    ]} />;
}

export function HRMShifts() {
  return <SimpleTableCRUD
    title="Office Shifts" breadcrumb="HRM / Office Shift" endpoint="/t/hrm/shifts" testidBase="hrm-shift" icon={Clock}
    fields={[
      { name: "name", lbl: "Shift Name", required: true, placeholder: "e.g. Day Shift" },
      { name: "start_time", lbl: "Start Time", type: "time", default: "09:00" },
      { name: "end_time", lbl: "End Time", type: "time", default: "18:00" },
    ]}
    columns={[
      { key: "name", lbl: "Shift", render: (r) => <span className="font-bold text-gray-900">{r.name}</span> },
      { key: "start_time", lbl: "Start Time", render: (r) => <span className="font-mono text-gray-600 bg-gray-50 px-2 py-1 rounded">{r.start_time}</span> },
      { key: "end_time", lbl: "End Time", render: (r) => <span className="font-mono text-gray-600 bg-gray-50 px-2 py-1 rounded">{r.end_time}</span> },
    ]} />;
}

export function HRMHolidays() {
  return <SimpleTableCRUD
    title="Holidays" breadcrumb="HRM / Holidays" endpoint="/t/hrm/holidays" testidBase="hrm-hol" icon={PartyPopper}
    fields={[
      { name: "name", lbl: "Holiday Name", required: true, placeholder: "e.g. New Year's Day" },
      { name: "date", lbl: "Date", type: "date", required: true },
      { name: "description", lbl: "Description", type: "textarea" },
    ]}
    columns={[
      { key: "name", lbl: "Holiday", render: (r) => <span className="font-bold text-gray-900">{r.name}</span> },
      { key: "date", lbl: "Date", render: (r) => <span className="font-medium text-gray-700">{r.date}</span> },
      { key: "description", lbl: "Description", render: (r) => <span className="text-gray-500 line-clamp-1">{r.description || "—"}</span> },
    ]} />;
}

// ────────────────────────── EMPLOYEES ──────────────────────────
export function HRMEmployees() {
  const { subdomain } = useParams();
  const [rows, setRows] = useState([]);
  const [depts, setDepts] = useState([]);
  const [desigs, setDesigs] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState({});
  
  const load = () => api.get("/t/hrm/employees").then((r) => setRows(r.data));
  useEffect(() => {
    load();
    api.get("/t/hrm/departments").then((r) => setDepts(r.data));
    api.get("/t/hrm/designations").then((r) => setDesigs(r.data));
    api.get("/t/hrm/shifts").then((r) => setShifts(r.data));
  }, []);

  const openCreate = () => { setForm({ first_name: "", last_name: "", email: "", phone: "", status: "active", salary: 0, date_joined: new Date().toISOString().slice(0, 10) }); setEdit(null); setOpen(true); };
  const openEdit = (r) => { setForm({ ...r }); setEdit(r); setOpen(true); };
  const submit = async () => {
    try {
      if (edit) await api.patch(`/t/hrm/employees/${edit.id}`, form);
      else await api.post("/t/hrm/employees", form);
      toast.success(edit ? "Employee updated" : "Employee created");
      setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const del = async (r) => {
    if (!window.confirm(`Delete ${r.name}?`)) return;
    await api.delete(`/t/hrm/employees/${r.id}`);
    toast.success("Employee deleted"); load();
  };

  const statusColors = {
    active: "bg-emerald-50 text-emerald-700 ring-emerald-600/20",
    inactive: "bg-amber-50 text-amber-700 ring-amber-600/20",
    terminated: "bg-rose-50 text-rose-700 ring-rose-600/20",
  };

  return (
    <div data-testid="hrm-emp-page" className="p-4 md:p-6 lg:p-8 min-h-screen bg-gray-50/50">
      <div className="max-w-[1400px] mx-auto">
        <PageHeader title="Employees Directory" breadcrumb="HRM / Employees" actions={
          <PrimaryButton icon={Plus} onClick={openCreate} testid="hrm-emp-new" className="shadow-sm">Add Employee</PrimaryButton>
        } />
        
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden mt-6">
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead className="bg-gray-50 text-gray-500 text-xs uppercase tracking-wider font-semibold border-b border-gray-100">
                <tr>
                  <th className="px-6 py-4">Employee</th>
                  <th className="px-6 py-4">Role & Dept</th>
                  <th className="px-6 py-4">Contact</th>
                  <th className="px-6 py-4 text-right">Salary</th>
                  <th className="px-6 py-4">Status</th>
                  <th className="px-6 py-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.map((r) => (
                  <tr key={r.id} className="hover:bg-gray-50/80 transition-colors group">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#1FA868]/20 to-[#1FA868]/5 border border-[#1FA868]/20 text-[#1FA868] font-bold text-sm flex items-center justify-center shadow-sm">
                          {(r.name || "?").slice(0, 2).toUpperCase()}
                        </div>
                        <div>
                          <div className="font-bold text-gray-900">{r.name}</div>
                          <div className="text-xs text-gray-500 mt-0.5">Joined {r.date_joined || "—"}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="font-medium text-gray-800">{r.designation_name || "—"}</div>
                      <div className="text-xs text-gray-500 mt-0.5">{r.department_name || "—"}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-gray-800 truncate max-w-[180px]">{r.email || "—"}</div>
                      <div className="text-xs text-gray-500 mt-0.5">{r.phone || "—"}</div>
                    </td>
                    <td className="px-6 py-4 text-right font-semibold text-gray-900">{fmt(r.salary)}</td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider ring-1 ring-inset ${statusColors[r.status] || "bg-gray-50 text-gray-600 ring-gray-500/10"}`}>
                        {r.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right opacity-0 group-hover:opacity-100 transition-opacity">
                      <button onClick={() => openEdit(r)} className="p-2 rounded-lg text-gray-400 hover:text-[#1FA868] hover:bg-emerald-50 transition-colors mr-1"><Pencil size={16} /></button>
                      <button onClick={() => del(r)} className="p-2 rounded-lg text-gray-400 hover:text-rose-600 hover:bg-rose-50 transition-colors"><Trash2 size={16} /></button>
                    </td>
                  </tr>
                ))}
                {!rows.length && <tr><td colSpan={6} className="px-6 py-12 text-center text-gray-400">No employees registered yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>

        {open && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-900/40 backdrop-blur-sm animate-in fade-in duration-200">
            <div className="bg-white rounded-3xl w-full max-w-3xl shadow-[0_20px_50px_rgb(0,0,0,0.1)] overflow-hidden flex flex-col max-h-[90vh]">
              <div className="flex items-center justify-between p-6 border-b border-gray-100 bg-gray-50/50">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-emerald-100 text-[#1FA868] rounded-lg"><Users size={20} /></div>
                  <h3 className="font-bold text-gray-900 text-xl tracking-tight">{edit ? "Edit Employee Profile" : "New Employee Profile"}</h3>
                </div>
                <button onClick={() => setOpen(false)} className="p-2 hover:bg-gray-200 text-gray-500 rounded-lg transition-colors"><X size={20} /></button>
              </div>
              
              <div className="p-6 overflow-y-auto custom-scrollbar">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5">
                  <SectionHeader title="Personal Information" className="col-span-full mb-2" />
                  <Field lbl="First Name" required><TextInput value={form.first_name} onChange={(v) => setForm((x) => ({ ...x, first_name: v }))} testid="hrm-emp-fn" placeholder="John" /></Field>
                  <Field lbl="Last Name" required><TextInput value={form.last_name} onChange={(v) => setForm((x) => ({ ...x, last_name: v }))} testid="hrm-emp-ln" placeholder="Doe" /></Field>
                  <Field lbl="Email Address"><TextInput type="email" value={form.email} onChange={(v) => setForm((x) => ({ ...x, email: v }))} testid="hrm-emp-email" placeholder="john.doe@company.com" /></Field>
                  <Field lbl="Phone Number"><TextInput type="tel" value={form.phone} onChange={(v) => setForm((x) => ({ ...x, phone: v }))} testid="hrm-emp-phone" placeholder="+1 (555) 000-0000" /></Field>
                  <Field lbl="Home Address" className="col-span-full">
                    <textarea value={form.address || ""} onChange={(e) => setForm((x) => ({ ...x, address: e.target.value }))}
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#1FA868] focus:ring-4 focus:ring-[#1FA868]/10 outline-none text-sm text-gray-900 bg-gray-50 focus:bg-white transition-all min-h-[80px] resize-y" placeholder="Full residential address..." />
                  </Field>

                  <div className="col-span-full w-full h-px bg-gray-100 my-2" />
                  <SectionHeader title="Employment Details" className="col-span-full mb-2" />
                  
                  <Field lbl="Department"><Select value={form.department_id || ""} onChange={(v) => setForm((x) => ({ ...x, department_id: v }))} options={depts.map((d) => ({ value: d.id, label: d.name }))} /></Field>
                  <Field lbl="Designation"><Select value={form.designation_id || ""} onChange={(v) => setForm((x) => ({ ...x, designation_id: v }))} options={desigs.map((d) => ({ value: d.id, label: d.name }))} /></Field>
                  <Field lbl="Assigned Shift"><Select value={form.shift_id || ""} onChange={(v) => setForm((x) => ({ ...x, shift_id: v }))} options={shifts.map((s) => ({ value: s.id, label: s.name }))} /></Field>
                  <Field lbl="Date Joined"><TextInput type="date" value={form.date_joined} onChange={(v) => setForm((x) => ({ ...x, date_joined: v }))} /></Field>
                  
                  <Field lbl="Base Salary"><TextInput type="number" value={form.salary} onChange={(v) => setForm((x) => ({ ...x, salary: Number(v) }))} testid="hrm-emp-sal" placeholder="0.00" /></Field>
                  <Field lbl="Account Status"><Select value={form.status || "active"} onChange={(v) => setForm((x) => ({ ...x, status: v }))} options={[{ value: "active", label: "Active" }, { value: "inactive", label: "Inactive" }, { value: "terminated", label: "Terminated" }]} /></Field>
                </div>
              </div>
              
              <div className="flex justify-end gap-3 p-6 border-t border-gray-100 bg-gray-50/50">
                <button onClick={() => setOpen(false)} className="px-5 py-2.5 rounded-xl text-sm font-semibold text-gray-700 hover:bg-gray-200 transition-colors">Cancel</button>
                <PrimaryButton icon={Save} onClick={submit} testid="hrm-emp-save" className="px-6 shadow-md shadow-emerald-500/20 text-sm">Save Employee</PrimaryButton>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ────────────────────────── ATTENDANCE ──────────────────────────
export function HRMAttendance() {
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [employees, setEmployees] = useState([]);
  const [records, setRecords] = useState([]);
  
  useEffect(() => { api.get("/t/hrm/employees").then((r) => setEmployees(r.data)); }, []);
  useEffect(() => { api.get("/t/hrm/attendance", { params: { date } }).then((r) => setRecords(r.data)); }, [date]);
  
  const mark = async (employee_id, status) => {
    await api.post("/t/hrm/attendance", { employee_id, date, status });
    toast.success(`Marked as ${status}`);
    api.get("/t/hrm/attendance", { params: { date } }).then((r) => setRecords(r.data));
  };
  
  const statusFor = (eid) => records.find((r) => r.employee_id === eid)?.status;
  
  const badgeStyles = {
    present: "bg-emerald-50 text-emerald-700 ring-emerald-600/20",
    absent: "bg-rose-50 text-rose-700 ring-rose-600/20",
    late: "bg-amber-50 text-amber-700 ring-amber-600/20",
    "half-day": "bg-sky-50 text-sky-700 ring-sky-600/20",
    leave: "bg-violet-50 text-violet-700 ring-violet-600/20"
  };

  return (
    <div data-testid="hrm-att-page" className="p-4 md:p-6 lg:p-8 min-h-screen bg-gray-50/50">
      <div className="max-w-5xl mx-auto">
        <PageHeader title="Daily Attendance" breadcrumb="HRM / Attendance" actions={
          <div className="flex items-center gap-3 bg-white p-1 rounded-xl shadow-sm border border-gray-200">
            <div className="pl-3 text-gray-400"><CalendarCheck size={18} /></div>
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="hrm-att-date" 
              className="border-none bg-transparent outline-none text-sm font-medium text-gray-700 py-2 pr-3 cursor-pointer" />
          </div>
        } />
        
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden mt-6">
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead className="bg-gray-50 text-gray-500 text-xs uppercase tracking-wider font-semibold border-b border-gray-100">
                <tr>
                  <th className="px-6 py-4">Employee</th>
                  <th className="px-6 py-4">Department</th>
                  <th className="px-6 py-4">Current Status</th>
                  <th className="px-6 py-4 text-right">Quick Mark</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {employees.map((e) => {
                  const s = statusFor(e.id);
                  return (
                    <tr key={e.id} className="hover:bg-gray-50/50 transition-colors">
                      <td className="px-6 py-4 font-bold text-gray-900">{e.name}</td>
                      <td className="px-6 py-4 text-gray-600">{e.department_name || "—"}</td>
                      <td className="px-6 py-4">
                        {s ? (
                          <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider ring-1 ring-inset ${badgeStyles[s]}`}>
                            {s.replace("-", " ")}
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider bg-gray-100 text-gray-500 ring-1 ring-inset ring-gray-200">
                            Unmarked
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="inline-flex bg-gray-100/80 p-1 rounded-xl gap-1">
                          {["present", "late", "half-day", "absent", "leave"].map((st) => {
                            const isActive = s === st;
                            return (
                              <button key={st} onClick={() => mark(e.id, st)} data-testid={`hrm-att-${e.id}-${st}`}
                                className={`text-[11px] font-semibold px-3 py-1.5 rounded-lg capitalize transition-all ${
                                  isActive 
                                    ? "bg-white text-gray-900 shadow-sm ring-1 ring-gray-200" 
                                    : "text-gray-500 hover:text-gray-900 hover:bg-gray-200/50"
                                }`}>
                                {st.replace("-", " ")}
                              </button>
                            );
                          })}
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {!employees.length && <tr><td colSpan={4} className="px-6 py-12 text-center text-gray-400">No employees available. Please create employees first.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

// ────────────────────────── LEAVE REQUESTS ──────────────────────────
export function HRMLeaves() {
  const [rows, setRows] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  
  const load = () => api.get("/t/hrm/leaves").then((r) => setRows(r.data));
  useEffect(() => { load(); api.get("/t/hrm/employees").then((r) => setEmployees(r.data)); }, []);
  
  const submit = async () => {
    try {
      await api.post("/t/hrm/leaves", form);
      toast.success("Leave request submitted"); setOpen(false); setForm({}); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const review = async (r, status) => {
    await api.patch(`/t/hrm/leaves/${r.id}/review`, { status });
    toast.success(`Request ${status}`); load();
  };
  const del = async (r) => {
    if (!window.confirm("Are you sure you want to delete this leave request?")) return;
    await api.delete(`/t/hrm/leaves/${r.id}`);
    toast.success("Request deleted"); load();
  };

  const statusColors = {
    pending: "bg-amber-50 text-amber-700 ring-amber-600/20",
    approved: "bg-emerald-50 text-emerald-700 ring-emerald-600/20",
    rejected: "bg-rose-50 text-rose-700 ring-rose-600/20"
  };

  return (
    <div data-testid="hrm-leave-page" className="p-4 md:p-6 lg:p-8 min-h-screen bg-gray-50/50">
      <div className="max-w-[1200px] mx-auto">
        <PageHeader title="Leave Requests" breadcrumb="HRM / Leave Management" actions={
          <PrimaryButton icon={Plus} onClick={() => { setForm({ leave_type: "annual", from_date: new Date().toISOString().slice(0, 10), to_date: new Date().toISOString().slice(0, 10) }); setOpen(true); }} testid="hrm-leave-new" className="shadow-sm">Log Request</PrimaryButton>
        } />
        
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden mt-6">
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead className="bg-gray-50 text-gray-500 text-xs uppercase tracking-wider font-semibold border-b border-gray-100">
                <tr>
                  <th className="px-6 py-4">Employee</th>
                  <th className="px-6 py-4">Leave Details</th>
                  <th className="px-6 py-4">Duration</th>
                  <th className="px-6 py-4">Status</th>
                  <th className="px-6 py-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.map((r) => (
                  <tr key={r.id} className="hover:bg-gray-50/80 transition-colors group">
                    <td className="px-6 py-4 font-bold text-gray-900">{r.employee_name || "—"}</td>
                    <td className="px-6 py-4">
                      <div className="font-medium text-gray-800 capitalize">{r.leave_type} Leave</div>
                      <div className="text-xs text-gray-500 mt-0.5 line-clamp-1 max-w-xs" title={r.reason}>{r.reason || "No reason provided"}</div>
                    </td>
                    <td className="px-6 py-4 text-gray-600 font-medium whitespace-nowrap">
                      {r.from_date} <span className="text-gray-400 mx-1">→</span> {r.to_date}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider ring-1 ring-inset ${statusColors[r.status]}`}>
                        {r.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        {r.status === "pending" && (
                          <div className="flex items-center gap-1 bg-gray-50 p-1 rounded-xl ring-1 ring-gray-200">
                            <button onClick={() => review(r, "approved")} data-testid={`hrm-leave-approve-${r.id}`} className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-emerald-50 text-emerald-700 hover:bg-emerald-100 transition-colors">Approve</button>
                            <button onClick={() => review(r, "rejected")} data-testid={`hrm-leave-reject-${r.id}`} className="text-xs font-semibold px-3 py-1.5 rounded-lg text-rose-600 hover:bg-rose-50 transition-colors">Reject</button>
                          </div>
                        )}
                        <button onClick={() => del(r)} className="p-2 rounded-lg text-gray-400 hover:text-rose-600 hover:bg-rose-50 transition-colors opacity-0 group-hover:opacity-100"><Trash2 size={16} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
                {!rows.length && <tr><td colSpan={5} className="px-6 py-12 text-center text-gray-400">No leave requests found.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>

        {open && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-900/40 backdrop-blur-sm animate-in fade-in duration-200">
            <div className="bg-white rounded-2xl w-full max-w-md shadow-[0_20px_50px_rgb(0,0,0,0.1)] overflow-hidden flex flex-col">
              <div className="flex items-center justify-between p-5 border-b border-gray-100 bg-gray-50/50">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-emerald-100 text-[#1FA868] rounded-lg"><CalendarX size={18} /></div>
                  <h3 className="font-bold text-gray-900 text-lg tracking-tight">New Leave Request</h3>
                </div>
                <button onClick={() => setOpen(false)} className="p-2 hover:bg-gray-200 text-gray-500 rounded-lg transition-colors"><X size={18} /></button>
              </div>
              
              <div className="p-6 space-y-4">
                <Field lbl="Employee" required><Select value={form.employee_id || ""} onChange={(v) => setForm((x) => ({ ...x, employee_id: v }))} options={employees.map((e) => ({ value: e.id, label: e.name }))} testid="hrm-leave-emp" /></Field>
                <Field lbl="Leave Type"><Select value={form.leave_type || "annual"} onChange={(v) => setForm((x) => ({ ...x, leave_type: v }))} options={["annual", "sick", "casual", "unpaid", "maternity"].map((t) => ({ value: t, label: t.charAt(0).toUpperCase() + t.slice(1) }))} /></Field>
                <div className="grid grid-cols-2 gap-4">
                  <Field lbl="Start Date" required><TextInput type="date" value={form.from_date} onChange={(v) => setForm((x) => ({ ...x, from_date: v }))} testid="hrm-leave-from" /></Field>
                  <Field lbl="End Date" required><TextInput type="date" value={form.to_date} onChange={(v) => setForm((x) => ({ ...x, to_date: v }))} testid="hrm-leave-to" /></Field>
                </div>
                <Field lbl="Reason / Notes">
                  <textarea value={form.reason || ""} onChange={(e) => setForm((x) => ({ ...x, reason: e.target.value }))} placeholder="Briefly explain the reason for leave..."
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#1FA868] focus:ring-4 focus:ring-[#1FA868]/10 outline-none text-sm text-gray-900 bg-gray-50 focus:bg-white transition-all min-h-[90px] resize-none" />
                </Field>
              </div>
              
              <div className="flex justify-end gap-3 p-5 border-t border-gray-100 bg-gray-50/50">
                <button onClick={() => setOpen(false)} className="px-4 py-2.5 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-200 transition-colors">Cancel</button>
                <PrimaryButton icon={Save} onClick={submit} testid="hrm-leave-save" className="shadow-sm">Submit Request</PrimaryButton>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ────────────────────────── PAYROLL ──────────────────────────
export function HRMPayroll() {
  const [rows, setRows] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  
  const load = () => api.get("/t/hrm/payrolls").then((r) => setRows(r.data));
  useEffect(() => { load(); api.get("/t/hrm/employees").then((r) => setEmployees(r.data)); }, []);
  
  const openCreate = () => {
    const period = new Date().toISOString().slice(0, 7);
    setForm({ period, basic: 0, allowances: 0, deductions: 0, bonus: 0, tax: 0 });
    setOpen(true);
  };
  
  const onEmployee = (id) => {
    const emp = employees.find((e) => e.id === id);
    setForm((f) => ({ ...f, employee_id: id, basic: emp?.salary || 0 }));
  };
  
  const submit = async () => {
    try {
      await api.post("/t/hrm/payrolls", form);
      toast.success("Payroll generated successfully"); setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  
  const pay = async (r) => {
    await api.patch(`/t/hrm/payrolls/${r.id}/pay`);
    toast.success("Payroll marked as paid"); load();
  };
  
  const del = async (r) => {
    if (!window.confirm("Are you sure you want to delete this payroll record?")) return;
    await api.delete(`/t/hrm/payrolls/${r.id}`);
    toast.success("Payroll deleted"); load();
  };

  return (
    <div data-testid="hrm-pay-page" className="p-4 md:p-6 lg:p-8 min-h-screen bg-gray-50/50">
      <div className="max-w-[1400px] mx-auto">
        <PageHeader title="Payroll Management" breadcrumb="HRM / Payroll" actions={
          <PrimaryButton icon={Plus} onClick={openCreate} testid="hrm-pay-new" className="shadow-sm">Generate Payslip</PrimaryButton>
        } />
        
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden mt-6">
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead className="bg-gray-50 text-gray-500 text-xs uppercase tracking-wider font-semibold border-b border-gray-100">
                <tr>
                  <th className="px-6 py-4">Employee</th>
                  <th className="px-6 py-4">Pay Period</th>
                  <th className="px-6 py-4 text-right">Basic</th>
                  <th className="px-6 py-4 text-right">Allowances</th>
                  <th className="px-6 py-4 text-right">Deductions</th>
                  <th className="px-6 py-4 text-right">Net Payable</th>
                  <th className="px-6 py-4">Status</th>
                  <th className="px-6 py-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.map((r) => (
                  <tr key={r.id} className="hover:bg-gray-50/80 transition-colors group">
                    <td className="px-6 py-4 font-bold text-gray-900">{r.employee_name || "—"}</td>
                    <td className="px-6 py-4 font-medium text-gray-600">{r.period}</td>
                    <td className="px-6 py-4 text-right text-gray-600">{fmt(r.basic)}</td>
                    <td className="px-6 py-4 text-right text-gray-600">{fmt(r.allowances)}</td>
                    <td className="px-6 py-4 text-right text-gray-600">{fmt(r.deductions)}</td>
                    <td className="px-6 py-4 text-right font-black text-[#1FA868] text-base">{fmt(r.net)}</td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider ring-1 ring-inset ${r.status === "paid" ? "bg-emerald-50 text-emerald-700 ring-emerald-600/20" : "bg-amber-50 text-amber-700 ring-amber-600/20"}`}>
                        {r.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        {r.status !== "paid" && (
                          <button onClick={() => pay(r)} data-testid={`hrm-pay-pay-${r.id}`} className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-emerald-50 text-emerald-700 hover:bg-emerald-100 transition-colors ring-1 ring-emerald-200">
                            Mark Paid
                          </button>
                        )}
                        <button onClick={() => del(r)} className="p-2 rounded-lg text-gray-400 hover:text-rose-600 hover:bg-rose-50 transition-colors opacity-0 group-hover:opacity-100"><Trash2 size={16} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
                {!rows.length && <tr><td colSpan={8} className="px-6 py-12 text-center text-gray-400">No payroll records found.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>

        {open && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-900/40 backdrop-blur-sm animate-in fade-in duration-200">
            <div className="bg-white rounded-2xl w-full max-w-lg shadow-[0_20px_50px_rgb(0,0,0,0.1)] overflow-hidden flex flex-col">
              <div className="flex items-center justify-between p-6 border-b border-gray-100 bg-gray-50/50">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-emerald-100 text-[#1FA868] rounded-lg"><Wallet size={20} /></div>
                  <h3 className="font-bold text-gray-900 text-xl tracking-tight">Generate Payroll</h3>
                </div>
                <button onClick={() => setOpen(false)} className="p-2 hover:bg-gray-200 text-gray-500 rounded-lg transition-colors"><X size={20} /></button>
              </div>
              
              <div className="p-6 overflow-y-auto custom-scrollbar">
                <div className="grid grid-cols-2 gap-x-5 gap-y-4">
                  <Field lbl="Employee" required className="col-span-2"><Select value={form.employee_id || ""} onChange={onEmployee} options={employees.map((e) => ({ value: e.id, label: e.name }))} testid="hrm-pay-emp" /></Field>
                  <Field lbl="Period (YYYY-MM)" required className="col-span-2"><TextInput value={form.period || ""} onChange={(v) => setForm((x) => ({ ...x, period: v }))} placeholder="e.g. 2026-04" /></Field>
                  
                  <div className="col-span-full my-1 border-t border-gray-100" />
                  
                  <Field lbl="Basic Salary"><TextInput type="number" value={form.basic} onChange={(v) => setForm((x) => ({ ...x, basic: Number(v) }))} placeholder="0.00" /></Field>
                  <Field lbl="Allowances"><TextInput type="number" value={form.allowances} onChange={(v) => setForm((x) => ({ ...x, allowances: Number(v) }))} placeholder="0.00" /></Field>
                  <Field lbl="Bonuses"><TextInput type="number" value={form.bonus} onChange={(v) => setForm((x) => ({ ...x, bonus: Number(v) }))} placeholder="0.00" /></Field>
                  
                  <div className="col-span-full my-1 border-t border-gray-100" />
                  
                  <Field lbl="Deductions"><TextInput type="number" value={form.deductions} onChange={(v) => setForm((x) => ({ ...x, deductions: Number(v) }))} placeholder="0.00" /></Field>
                  <Field lbl="Tax Deductions"><TextInput type="number" value={form.tax} onChange={(v) => setForm((x) => ({ ...x, tax: Number(v) }))} placeholder="0.00" /></Field>
                  
                  <div className="col-span-2 mt-4 bg-gradient-to-br from-[#E8F5E9] to-emerald-50 border border-emerald-100 rounded-xl p-5 flex items-center justify-between shadow-inner">
                    <span className="text-emerald-900 font-semibold text-sm uppercase tracking-wide">Net Salary Payable</span>
                    <span className="font-black text-2xl text-[#1FA868] drop-shadow-sm">
                      {fmt((form.basic || 0) + (form.allowances || 0) + (form.bonus || 0) - (form.deductions || 0) - (form.tax || 0))}
                    </span>
                  </div>
                </div>
              </div>
              
              <div className="flex justify-end gap-3 p-6 border-t border-gray-100 bg-gray-50/50">
                <button onClick={() => setOpen(false)} className="px-5 py-2.5 rounded-xl text-sm font-semibold text-gray-700 hover:bg-gray-200 transition-colors">Cancel</button>
                <PrimaryButton icon={Save} onClick={submit} testid="hrm-pay-save" className="px-6 shadow-md shadow-emerald-500/20 text-sm">Save & Generate</PrimaryButton>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}